import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";

interface PushTokenRow {
  id: string;
  user_id: string;
  token: string;
  platform: string;
  device_name: string | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  profiles: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

interface UserSummary {
  id: string;
  full_name: string;
  has_active_token: boolean;
  tokens: PushTokenRow[];
}

/**
 * Format a date string to a readable "time ago" format in Dutch
 */
const formatTimeAgo = (dateStr: string | null): string => {
  if (!dateStr) return "Nooit";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Zojuist";
  if (minutes < 60) return `${minutes}m geleden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d geleden`;
  return `${Math.floor(days / 30)}mnd geleden`;
};

/**
 * Super Admin Push Token Status Page
 */
export default function PushTokenStatusScreen() {
  const colors = useThemeColors();

  const [users, setUsers] = useState<UserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sendingTestTo, setSendingTestTo] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [{ data: allProfiles }, { data: allTokens }] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, full_name")
          .eq("is_dependent", false)
          .order("full_name"),
        supabase
          .from("push_tokens")
          .select("*, profiles(full_name, avatar_url)")
          .order("last_used_at", { ascending: false }),
      ]);

      const tokensByUser = new Map<string, PushTokenRow[]>();
      (allTokens || []).forEach((t: PushTokenRow) => {
        const existing = tokensByUser.get(t.user_id) || [];
        existing.push(t);
        tokensByUser.set(t.user_id, existing);
      });

      const summary: UserSummary[] = (allProfiles || []).map((p) => {
        const tokens = tokensByUser.get(p.id) || [];
        return {
          id: p.id,
          full_name: p.full_name || "Onbekend",
          has_active_token: tokens.some((t) => t.is_active),
          tokens,
        };
      });

      setUsers(summary);
    } catch (err) {
      console.error("Error fetching push token data:", err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchData();
  }, [fetchData]);

  /**
   * Send a test push notification to a specific user
   */
  const handleSendTestPush = async (userId: string, userName: string) => {
    setSendingTestTo(userId);

    try {
      const { data: tokens } = await supabase
        .from("push_tokens")
        .select("token")
        .eq("user_id", userId)
        .eq("is_active", true);

      if (!tokens || tokens.length === 0) {
        Alert.alert("Geen token", `${userName} heeft geen actieve push token.`);
        return;
      }

      const pushMessages = tokens.map((t) => ({
        to: t.token,
        title: "Test Notificatie",
        body: "Dit is een test push notificatie van de admin.",
        sound: "default",
        data: { type: "test" },
      }));

      const response = await fetch("https://exp.host/--/api/v2/push/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(pushMessages),
      });

      const result = await response.json();

      const hasError = result.data?.some((t: any) => t.status === "error");
      if (hasError) {
        Alert.alert("Deels mislukt", "Sommige tokens gaven een fout. Controleer de logs.");
      } else {
        Alert.alert("Verzonden", `Test push gestuurd naar ${userName}.`);
      }
    } catch (err) {
      console.error("Error sending test push:", err);
      Alert.alert("Fout", "Kon test push niet versturen.");
    } finally {
      setSendingTestTo(null);
    }
  };

  const activeCount = users.filter((u) => u.has_active_token).length;
  const totalCount = users.length;

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView
        className="flex-1 px-4"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Summary Card */}
        <View
          className="rounded-2xl p-4 mt-4 flex-row items-center"
          style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
        >
          <View
            className="w-12 h-12 rounded-full items-center justify-center mr-4"
            style={{ backgroundColor: activeCount === totalCount ? "#D1FAE5" : "#FEF3C7" }}
          >
            <Ionicons
              name={activeCount === totalCount ? "checkmark-circle" : "warning"}
              size={24}
              color={activeCount === totalCount ? "#10B981" : "#F59E0B"}
            />
          </View>
          <View className="flex-1">
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              {activeCount} / {totalCount} gebruikers
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {activeCount === totalCount
                ? "Alle gebruikers hebben een actieve push token"
                : `${totalCount - activeCount} gebruiker(s) zonder actieve token`}
            </Text>
          </View>
        </View>

        {/* User List */}
        <View className="mt-6">
          <Text
            className="text-sm font-semibold uppercase tracking-wide mb-3"
            style={{ color: colors.textTertiary }}
          >
            Gebruikers
          </Text>

          {users.map((user) => {
            const activeToken = user.tokens.find((t) => t.is_active);
            const isSending = sendingTestTo === user.id;

            return (
              <View
                key={user.id}
                className="rounded-xl p-4 mb-3"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <View className="flex-row items-center">
                  {/* Status indicator */}
                  <View
                    className="w-3 h-3 rounded-full mr-3"
                    style={{
                      backgroundColor: user.has_active_token ? "#10B981" : "#EF4444",
                    }}
                  />
                  <View className="flex-1">
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      {user.full_name}
                    </Text>
                    {activeToken ? (
                      <View className="flex-row items-center mt-1">
                        <Ionicons
                          name={activeToken.platform === "ios" ? "logo-apple" : "logo-android"}
                          size={14}
                          color={colors.textTertiary}
                        />
                        <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                          {activeToken.device_name || activeToken.platform} ·{" "}
                          {formatTimeAgo(activeToken.last_used_at)}
                        </Text>
                      </View>
                    ) : (
                      <Text className="text-xs mt-1" style={{ color: "#EF4444" }}>
                        Geen actieve push token
                      </Text>
                    )}
                  </View>

                  {/* Test push button */}
                  <Pressable
                    onPress={() => handleSendTestPush(user.id, user.full_name)}
                    disabled={!user.has_active_token || isSending}
                    className="rounded-lg px-3 py-2"
                    style={{
                      backgroundColor: user.has_active_token ? colors.primary + "15" : colors.border,
                      opacity: user.has_active_token ? 1 : 0.4,
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`Stuur test push naar ${user.full_name}`}
                  >
                    {isSending ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons
                        name="paper-plane-outline"
                        size={16}
                        color={user.has_active_token ? colors.primary : colors.textTertiary}
                      />
                    )}
                  </Pressable>
                </View>

                {/* Show all tokens for this user if they have multiple */}
                {user.tokens.length > 1 && (
                  <View className="mt-3 pt-3" style={{ borderTopWidth: 1, borderTopColor: colors.border }}>
                    <Text className="text-xs font-medium mb-2" style={{ color: colors.textTertiary }}>
                      Alle tokens ({user.tokens.length})
                    </Text>
                    {user.tokens.map((t) => (
                      <View key={t.id} className="flex-row items-center mb-1">
                        <View
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: t.is_active ? "#10B981" : "#9CA3AF" }}
                        />
                        <Text className="text-xs flex-1" style={{ color: colors.textSecondary }}>
                          {t.device_name || t.platform} · {t.is_active ? "Actief" : "Inactief"} ·{" "}
                          {formatTimeAgo(t.last_used_at)}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            );
          })}
        </View>

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}
